import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh,
  Save,
  Settings as SettingsIcon,
  Numbers,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  buildSequencePreview,
  defaultSystemSettingsFormData,
  mapSettingsToSystemSettingsFormData,
  settingsCardSx,
  SystemSettingsFormData,
} from '../settings/companySettingsShared';
import { COMPANY_SETTINGS_QUERY_KEY, useCompanySettingsQuery } from '../settings/useCompanySettingsQuery';
import { SettingsPageHeader } from '../settings/SettingsPageHeader';

export const SystemSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  const [isDirty, setIsDirty] = useState(false);
  const [sequenceData, setSequenceData] = useState<SystemSettingsFormData>(
    defaultSystemSettingsFormData
  );

  const settingsQuery = useCompanySettingsQuery(companyId);

  useEffect(() => {
    setSequenceData(mapSettingsToSystemSettingsFormData(settingsQuery.data));
    setIsDirty(false);
  }, [settingsQuery.data]);

  const updateSequenceMutation = useMutation(
    (data: SystemSettingsFormData) => companyApi.updateSequenceNumbers(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([COMPANY_SETTINGS_QUERY_KEY, companyId]);
        toast.success(t('settings.messages.sequencesUpdated'));
        setIsDirty(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('settings.messages.sequencesUpdateFailed'));
      },
    }
  );

  const handleSequenceChange = (
    field: keyof SystemSettingsFormData,
    value: number
  ) => {
    setSequenceData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSaveSequences = () => {
    updateSequenceMutation.mutate(sequenceData);
  };

  const numberRangeRows = useMemo(
    () => [
      {
        key: 'reservationNumber' as const,
        label: t('fields.reservation', { defaultValue: 'Reservation' }),
        description: t('settings.numberingReservationDescription', {
          defaultValue: 'Used for reservation and rental workflow documents.',
        }),
        yearPattern: 'yy',
        counterPattern: '00000',
        currentValue: settingsQuery.data?.reservationSequence?.nextNumber,
        nextValue: sequenceData.reservationNumber,
        example: buildSequencePreview(sequenceData.reservationNumber),
      },
      {
        key: 'invoiceNumber' as const,
        label: t('fields.invoice', { defaultValue: 'Invoice' }),
        description: t('settings.numberingInvoiceDescription', {
          defaultValue: 'Used for customer billing and invoice generation.',
        }),
        yearPattern: 'yy',
        counterPattern: '00000',
        currentValue: settingsQuery.data?.invoiceSequence?.nextNumber,
        nextValue: sequenceData.invoiceNumber,
        example: buildSequencePreview(sequenceData.invoiceNumber),
      },
    ],
    [
      sequenceData.invoiceNumber,
      sequenceData.reservationNumber,
      settingsQuery.data?.invoiceSequence?.nextNumber,
      settingsQuery.data?.reservationSequence?.nextNumber,
      t,
    ]
  );

  if (settingsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <SettingsPageHeader
        icon={<SettingsIcon color="primary" />}
        title={t('settings.systemTitle', { defaultValue: 'System Settings' })}
        subtitle={t('settings.systemSubtitle', {
          defaultValue:
            'Control numbering sequences and other application-wide operational defaults for your company workspace.',
        })}
        actions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: { xs: '100%', lg: 'auto' } }}
          >
            <Tooltip title={t('common.refresh')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => settingsQuery.refetch()}
                  sx={{ minWidth: 150 }}
                >
                  {t('common.refresh')}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={
                updateSequenceMutation.isLoading ? <CircularProgress size={18} /> : <Save />
              }
              onClick={handleSaveSequences}
              disabled={!isDirty || updateSequenceMutation.isLoading}
              sx={{ minWidth: 180 }}
            >
              {t('settings.saveSequences')}
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack spacing={3}>
            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Numbers color="primary" />
                <Box>
                  <Typography variant="h6">
                    {t('settings.numberingSection', { defaultValue: 'Number Ranges' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.numberingHint', {
                      defaultValue:
                        'Set the next values used when the system generates reservation and invoice numbers.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.numberingTableHint', {
                  defaultValue:
                    'Examples update instantly as you adjust the next start number.',
                })}
              </Typography>

              <TableContainer
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflowX: 'auto',
                }}
              >
                <Table sx={{ minWidth: 780 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'grey.50',
                        '& .MuiTableCell-root': {
                          borderBottomColor: 'divider',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableCell>{t('fields.type')}</TableCell>
                      <TableCell>
                        {t('settings.numberingColumnPeriod', {
                          defaultValue: 'Year Pattern',
                        })}
                      </TableCell>
                      <TableCell>
                        {t('settings.numberingColumnPattern', {
                          defaultValue: 'Number Pattern',
                        })}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        {t('settings.numberingColumnNextStart', {
                          defaultValue: 'Next Start Number',
                        })}
                      </TableCell>
                      <TableCell>
                        {t('settings.numberingColumnCurrent', {
                          defaultValue: 'Current Saved',
                        })}
                      </TableCell>
                      <TableCell>
                        {t('settings.numberingColumnExample', {
                          defaultValue: 'Example',
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {numberRangeRows.map((row) => (
                      <TableRow
                        key={row.key}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          '& .MuiTableCell-root': {
                            verticalAlign: 'top',
                            py: 2,
                          },
                        }}
                      >
                        <TableCell sx={{ minWidth: 240 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {row.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, maxWidth: 260 }}
                          >
                            {row.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          >
                            {row.yearPattern}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          >
                            {row.counterPattern}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={row.nextValue}
                            onChange={(e) =>
                              handleSequenceChange(
                                row.key,
                                parseInt(e.target.value, 10) || 1
                              )
                            }
                            inputProps={{ min: 1 }}
                            label={t(
                              row.key === 'reservationNumber'
                                ? 'fields.nextReservationNumber'
                                : 'fields.nextInvoiceNumber'
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          >
                            {row.currentValue || t('common.none')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('common.current')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          >
                            {row.example}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('settings.numberingExampleHint', {
                              defaultValue: 'Live preview',
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {(settingsQuery.data?.reservationSequence || settingsQuery.data?.invoiceSequence) && (
                <Alert severity="info" sx={{ mt: 2.5 }}>
                  {t('settings.currentSequence', {
                    reservation: settingsQuery.data?.reservationSequence?.nextNumber,
                    invoice: settingsQuery.data?.invoiceSequence?.nextNumber,
                  })}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={
                    updateSequenceMutation.isLoading ? <CircularProgress size={18} /> : <Save />
                  }
                  onClick={handleSaveSequences}
                  disabled={!isDirty || updateSequenceMutation.isLoading}
                >
                  {t('settings.saveSequences')}
                </Button>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {settingsQuery.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t('common.error', { defaultValue: 'Error' })}
        </Alert>
      )}
    </Container>
  );
};
